/**
 * Mini Espace Coach — page publique privée par slug (/espace-coach/:slug)
 * Le coach voit sa fiche et dépose ses impressions/suggestions à Kru Ralph.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { API } from "../lib/api";
import axios from "axios";
import { toast } from "sonner";
import { CoachSpaceTasks } from "../components/CoachSpaceTasks";
import { Loader2, Award, GraduationCap, Target, Send, MessageSquare } from "lucide-react";

const pub = axios.create({ baseURL: API });

export default function CoachSpacePublic() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [msg, setMsg] = useState("");
  const [category, setCategory] = useState("general");
  const [rating, setRating] = useState(0);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await pub.get(`/coach-cockpit/space/${slug}`);
      setData(r.data);
    } catch (e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!msg.trim()) { toast.error("Écris ton impression d'abord"); return; }
    setSending(true);
    try {
      await pub.post(`/coach-cockpit/space/${slug}/impression`, {
        message: msg.trim(), category, rating: rating || null,
      });
      toast.success("Merci ! Ton impression a été transmise à Kru Ralph.");
      setMsg(""); setRating(0); setCategory("general");
      load();
    } catch (e) {
      toast.error("Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <Center><Loader2 className="animate-spin text-amber-400" size={32} /></Center>;
  if (notFound) return <Center><div className="text-center text-zinc-400"><div className="text-5xl mb-3">🔒</div><p>Espace introuvable.</p></div></Center>;

  const c = data.coach;
  const first = c.display_name || (c.full_name || "").split(" ")[0];

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="coach-space-public">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <CoachSpaceTasks slug={slug} />
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-500 to-red-600 flex items-center justify-center text-3xl font-bold mb-3 overflow-hidden">
            {c.photo_url ? <img src={c.photo_url} alt={c.full_name} className="w-full h-full object-cover" /> : (first[0] || "?")}
          </div>
          <div className="text-amber-400 text-xs tracking-widest uppercase mb-1">Mini Espace Coach · KAIZEN</div>
          <h1 className="text-3xl font-bold">Bienvenue, {first} 🥋</h1>
          <p className="text-zinc-400 text-sm mt-1">{c.title || c.role_level_label}</p>
        </div>

        {/* Fiche */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-amber-300 uppercase tracking-wide">Ta fiche</h2>
          <Row icon={<Award size={16} className="text-amber-400" />} label="Rôle" value={c.role_level_label} />
          {c.internal_rank && <Row icon={<Award size={16} className="text-amber-400" />} label="Rang interne" value={c.internal_rank} />}
          {(c.disciplines || []).length > 0 && (
            <Row icon={<GraduationCap size={16} className="text-amber-400" />} label="Disciplines" value={c.disciplines.map((d) => d.replace(/_/g, " ")).join(" · ")} />
          )}
          {(c.assigned_groups || []).length > 0 && (
            <Row icon={<GraduationCap size={16} className="text-amber-400" />} label="Groupes encadrés" value={c.assigned_groups.join(" · ")} />
          )}
          {c.pedagogical_strengths && <Row icon={<Target size={16} className="text-emerald-400" />} label="Forces" value={c.pedagogical_strengths} />}
          {c.monthly_kaizen_goal && (
            <div className="bg-amber-950/30 border border-amber-900/40 rounded-lg p-3">
              <div className="text-amber-300 text-xs font-semibold mb-1">🎯 Objectif Kaizen du mois</div>
              <div className="text-sm text-amber-100">{c.monthly_kaizen_goal}</div>
            </div>
          )}
          {c.philosophy && <p className="text-zinc-300 text-sm italic border-l-2 border-amber-600 pl-3">"{c.philosophy}"</p>}
        </div>

        {/* 📋 Résumés de rencontre d'équipe */}
        {(data.broadcasts || []).length > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 mt-5" data-testid="coach-broadcasts">
            <h2 className="text-sm font-semibold text-amber-300 uppercase tracking-wide flex items-center gap-2 mb-3">
              📋 Résumés de rencontre
            </h2>
            <div className="space-y-3">
              {(data.broadcasts || []).map((b) => (
                <details key={b.id} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`coach-broadcast-${b.id}`}
                  onToggle={(e) => { if (e.target.open && !b.read_by_me) { pub.post(`/coach-cockpit/space/${slug}/broadcast/${b.id}/read`).catch(() => {}); } }}>
                  <summary className="cursor-pointer text-sm font-semibold text-white flex items-center justify-between">
                    <span>{b.title}</span>
                    <span className="text-xs text-zinc-500 font-normal">{b.meeting_date}</span>
                  </summary>
                  <div className="mt-3 space-y-3 text-sm">
                    {b.intro && <p className="text-zinc-300 italic border-l-2 border-amber-600 pl-3">{b.intro}</p>}
                    {b.next_meeting && (
                      <div className="bg-amber-950/30 border border-amber-900/40 rounded-lg p-3 text-amber-100 text-xs">
                        <span className="font-semibold">📅 Prochaine rencontre : </span>{b.next_meeting}
                      </div>
                    )}
                    {(b.sections || []).map((s, i) => (
                      <div key={i}>
                        <div className="text-amber-300 text-xs font-semibold uppercase tracking-wide mb-1">{s.heading}</div>
                        <ul className="list-disc list-inside space-y-1 text-zinc-300 text-[13px]">
                          {(s.items || []).map((it, j) => <li key={j}>{it}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        {/* Impressions */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 mt-5">
          <h2 className="text-sm font-semibold text-amber-300 uppercase tracking-wide flex items-center gap-2 mb-1">
            <MessageSquare size={16} /> Partage tes impressions
          </h2>
          <p className="text-zinc-400 text-xs mb-4">Qu'est-ce que tu aimerais voir dans ton espace ? Idées, besoins, suggestions — tout aide Kru Ralph à le construire pour toi.</p>

          <div className="flex gap-2 mb-3 flex-wrap">
            {[["general", "Général"], ["espace", "Mon espace"], ["suggestion", "Suggestion"], ["besoin", "Un besoin"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setCategory(k)}
                className={`text-xs px-3 py-1.5 rounded-full border ${category === k ? "bg-amber-600 border-amber-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-300"}`}
                data-testid={`impression-cat-${k}`}>
                {lbl}
              </button>
            ))}
          </div>

          <textarea
            value={msg} onChange={(e) => setMsg(e.target.value)}
            placeholder="Ex : J'aimerais voir mes cours de la semaine et la progression de mon groupe..."
            rows={4}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-sm text-white outline-none focus:border-amber-600"
            data-testid="impression-textarea"
          />

          <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
            <div className="flex items-center gap-1">
              <span className="text-xs text-zinc-400 mr-1">Note de l'idée :</span>
              {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                <button key={n} onClick={() => setRating(n === rating ? 0 : n)}
                  className={`w-6 h-6 text-[11px] rounded ${n <= rating ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-500"}`}>
                  {n}
                </button>
              ))}
            </div>
            <button onClick={submit} disabled={sending}
              className="px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              data-testid="impression-submit-btn">
              {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Envoyer à Kru Ralph
            </button>
          </div>

          {(data.my_impressions || []).length > 0 && (
            <div className="mt-6 border-t border-zinc-800 pt-4">
              <div className="text-xs text-zinc-500 mb-2">Tes impressions envoyées :</div>
              <div className="space-y-2">
                {data.my_impressions.map((im) => (
                  <div key={im.id} className="bg-zinc-800/50 rounded-lg p-2.5 text-sm text-zinc-200">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-300 mr-2">{im.category}</span>
                    {im.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-zinc-600 text-xs mt-8">Institut Impact Élite Martial · Système KAIZEN</p>
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">{children}</div>;
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <div className="mt-0.5">{icon}</div>
      <div><span className="text-zinc-500">{label} : </span><span className="text-zinc-100">{value}</span></div>
    </div>
  );
}
